import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from 'react-icons/md';
import { IoMdAdd } from 'react-icons/io';
import { Layout, Header } from '../../../../components';
import { handleApi, formatMediumDate, capitalize } from '../../../../utils/utils';
import AppContext from '../../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { userData, currentColor, setSuppressAutoNavigate } = useContext(AppContext);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();
  const itemsPerPage = 10;
  const dropdownRef = useRef(null);
  useEffect(() => {
    const timeout = setTimeout(() => fetchRoles(), 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const fetchRoles = async () => {
    const apiEndpoint = 'roles';
    setIsLoading(true);
    try {
      const response = await handleApi(apiEndpoint, {}, userData, 'GET', {});
      if (response.error) {
        toast.error(`Error: ${response.errorMessage}`);
        return;
      }
      console.log('response data sir',response.data)
      setRoles(response.data);

    } catch (error) {
      toast.error(error.message || 'An unexpected error occurred.');
    } finally {setIsLoading(false);}
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const debouncedSearchTerm = useMemo(() => debounce(handleSearchChange, 300), []);

  const filteredRoles = useMemo(() => {
    console.log('roles sir ',roles)
    return roles.filter((role) =>role?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [roles, searchTerm]);

  const sortedFilteredRoles = filteredRoles.sort(
    (a, b) => new Date(b.create_time) - new Date(a.create_time)
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = sortedFilteredRoles.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeleteClick = (item) => {setItemToDelete(item);setIsModalOpen(true);};

  const handleConfirmDelete = () => {
    handleAction('delete', itemToDelete);
    console.log("Item deleted!");
    setIsModalOpen(false);
  };

  const handleCancelDelete = () => {setIsModalOpen(false);};
  const handleAction = (action, role) => {
    setSuppressAutoNavigate(true); // prevent auto redirect
    switch (action) {
      case 'new':navigate(`/roles/new`, { state: { roles } }); break; // pass roles
      case 'view': navigate(`/roles/view/${role.id}`, { state: { initialRole: role } });break;
      case 'edit':navigate(`/roles/edit/${role.id}`,  {state: { initialRole:role, roles } });break;
      case 'delete':navigate(`/roles/delete/${role.id}`, { state: { initialRole: role } });break;
      default:toast.error(`Action "${action}" is not supported.`);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-white text-white  p-4 sm:p-8">
        {/* Header and Close Button */}
        <div className="relative flex justify-between items-center mb-4">
          <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center">
            ROLES
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <ToastContainer position="top-center" autoClose={2000} />
        
        {isLoading ? (
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Loading. Please wait...</p>
          </div>
        ) : (
         
          <div className="bg-gray-500 p-4 sm:p-6  rounded-3xl  border border-gray-300 mx-auto max-w-[60%]">
            {/* Search Input */}
            <div className="mb-2 flex justify-between">
              <div className="relative w-full sm:w-1/2">
                {/* Emoji on the Left */}
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg text-gray-500 select-none">🔍</span>

                {/* Input Field */}
                <input
                  type="text"
                  placeholder="Search role"
                  onChange={debouncedSearchTerm}
                  className="w-full py-2.5 pl-10 pr-4 rounded-xl bg-gray-200 text-gray-800 placeholder-gray-500 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white transition duration-300 text-sm sm:text-base"
                />
              </div>
              {/* add new button */}
              <button
                className="text-white px-2 py-2 text-sm font-semibold rounded-lg flex items-center gap-1 bg-blue-500 hover:bg-blue-700 transition-all duration-300"
                onClick={() => handleAction('new', {})}
              >
                <IoMdAdd size={20} />
                <span>New</span>
              </button>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap justify-center gap-1 mb-1 ">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => paginate(index + 1)}
                  className={`px-2  text-sm rounded-full font-semibold transition-colors duration-300
                              ${currentPage === index + 1 ? 'bg-cyan-500 text-white ' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {/* Role Cards Display */}
            <div className="space-y-2">
              {currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <div key={item.id} className="relative">
                    <div
                      onClick={() => setSelectedRowId(item.id === selectedRowId ? null : item.id)}
                      className="flex items-center gap-4 mx-5 px-2 py-1  hover:bg-gray-700 transition-all duration-200 
                       cursor-pointer rounded-2xl  border border-gray-100"
                    >
                    <div className="flex-shrink-0 flex flex-col items-center">
                      {/* Role Image */}
                      <img
                          className="rounded-full h-8 w-8 object-cover ring-2 ring-gray-700"
                          src={
                            item?.employees?.photo_file_name
                              ? `http://dev.neon-mw.com:3001/api/employees/photos/${item.employees.photo_file_name}`
                              : `https://placehold.co/100x100/1f2937/e5e7eb?text=${
                                  (item?.name?.[0] || '') 
                                }`
                          }
                          alt={`${item?.name || 'Role'} photo`}
                          onError={(e) => {
                            e.target.onerror = null; // Prevents infinite loop
                            e.target.src = `https://placehold.co/100x100/1f2937/e5e7eb?text=${
                                  (item?.name?.[0] || '') + (item?.name?.[0] || '')
                                }`;
                          }}
                        />
                      {/* Status */}
                      {/* <span 
                      className='mt-1 text-[8px] text-gray-200 font-medium truncate max-w-[60px]
                      text-yellow-400 bg-yellow-900'
                      
                        title={item?.role_roles?.name.toUpperCase() || 'N/A'}>{item?.role_roles?.name.toUpperCase() || 'N/A'}
                      </span> */}
                    </div>

                    <div className="flex-grow text-white text-sm leading-tight">
                      {/* Role name + price in one row */}
                      <div className="flex items-center justify-between">
                        <span className="mt-1 text-[13px] text-white font-medium " title={capitalize(item?.name) || ''}>
                        {capitalize(item?.name) || 'N/A'} 
                        </span>
                      </div>
                      <div className="flex-grow text-white text-sm">
                        <div className="flex flex-col">
                          {selectedRowId === item.id ? (
                            <div className="flex flex-col text-gray-300 text-[10px] leading-none">
                              <span className="my-1">{item?.description || ''}</span>
                            
                            </div>
                          ) : (
                            <span className="text-[10px] text-gray-300 leading-none">
                              {item?.description || ''}
                            </span>
                          )}
                        </div> 
                      </div>
                    </div>

                    {/* Conditionally render the menu or the arrow */}
                    <div className="flex-shrink-0 ml-auto flex flex-col items-end gap-1">
                      {selectedRowId === item.id ? (
                        item.approved === 1 ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction('view', item);
                            }}
                            className="px-1 text-xs rounded-lg bg-gray-600 hover:bg-gray-500 transition-all duration-300"
                          >
                            View
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAction('edit', item);
                              }}
                              className="px-1 text-xs rounded-lg bg-gray-600 hover:bg-gray-500 transition-all duration-300"
                            >
                              Edit
                            </button>
                           
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(item);
                                
                              }}
                              className="px-1  text-xs rounded-lg bg-red-600 hover:bg-red-500 transition-all duration-300"
                            >
                              Delete
                            </button>
                          </>
                        )
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transform transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-white/50 py-10">
                <p className="text-lg">No role found.</p>
              </div>
            )}
          </div>
            {console.log('Current Items for display sir :', currentItems)}    
        </div>
      )}
    </div>
    {/* The Delete Confirmation Modal */}
    {isModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Modal Overlay */}
        <div
          className="absolute inset-0 bg-gray-900 bg-opacity-75 transition-opacity duration-300"
            onClick={handleCancelDelete}
          ></div>

          {/* Modal Content */}
          <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden transform transition-all sm:max-w-md sm:w-full z-10 scale-100 opacity-100">
            <div className="px-1 py-2">
              <div className="flex flex-col items-center justify-center text-center">
                {/* Icon */}
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.013 21H7.987a2 2 0 01-1.92-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>

                {/* Title */}
                <h3 className="mt-2 text-2xl font-bold leading-6 text-gray-900">
                  Are you  sure?
                </h3>

                {/* Description */}
                <div className="mt-1 text-sm text-gray-500">
                  <p>
                    This will permanently delete this role.
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row-reverse sm:gap-4">
              <button
                type="button"
                className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
              <button
                type="button"
                className="w-full sm:w-auto mt-3 sm:mt-0 inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 sm:text-sm"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
export default Roles;